import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, Globe, MapPin, Loader2, Facebook, Instagram } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BRAND } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "bg-[#1E2227] border-[#A8AEB2]/25 text-white placeholder:text-[#A8AEB2]/60 focus-visible:ring-[#7AB648] focus-visible:ring-offset-0 h-12 rounded-sm";

const CONTACT_ITEMS = [
  { icon: Phone, label: BRAND.phone, href: BRAND.phoneHref, testid: "contact-phone" },
  { icon: Mail, label: BRAND.email, href: `mailto:${BRAND.email}`, testid: "contact-email" },
  { icon: Globe, label: BRAND.website, href: "#top", testid: "contact-website" },
  { icon: MapPin, label: BRAND.area, href: null, testid: "contact-area" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try calling us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative z-10 bg-[#1E2227] py-20 md:py-32"
      data-testid="contact-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
        {/* Left — big contact info */}
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="font-body text-sm uppercase tracking-[0.4em] text-[#7AB648]">
              Get In Touch
            </span>
            <h2 className="font-display uppercase text-5xl md:text-7xl text-white leading-[0.9] mt-4">
              Let&apos;s Talk
            </h2>
            <p className="mt-6 font-body text-base md:text-lg text-[#A8AEB2] max-w-md leading-relaxed">
              Have a question or ready to start? Reach out directly or drop us a
              message and we&apos;ll respond promptly.
            </p>
          </motion.div>

          <div className="mt-10 space-y-1">
            {CONTACT_ITEMS.map((item, i) => {
              const Icon = item.icon;
              const inner = (
                <div className="flex items-center gap-5 py-5 border-t border-[#A8AEB2]/15 group">
                  <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-[#14171A] text-[#7AB648] transition-colors group-hover:bg-[#7AB648] group-hover:text-[#14171A]">
                    <Icon size={20} />
                  </span>
                  <span className="font-head text-lg md:text-2xl text-white tracking-tight">
                    {item.label}
                  </span>
                </div>
              );
              return item.href ? (
                <a key={item.testid} href={item.href} data-testid={item.testid}>
                  {inner}
                </a>
              ) : (
                <div key={item.testid} data-testid={item.testid}>
                  {inner}
                </div>
              );
            })}
          </div>

          <div className="mt-8" data-testid="contact-socials">
            <span className="font-body text-sm uppercase tracking-[0.3em] text-[#A8AEB2]">
              Follow Us
            </span>
            <div className="mt-4 flex items-center gap-4">
              <a
                href={BRAND.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Gormans Contracting on Facebook"
                className="group flex items-center gap-3 rounded-sm border border-[#A8AEB2]/20 bg-[#14171A] px-5 py-3 transition-colors hover:border-[#7AB648]"
                data-testid="contact-facebook"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-sm bg-[#1E2227] text-[#7AB648] transition-colors group-hover:bg-[#7AB648] group-hover:text-[#14171A]">
                  <Facebook size={18} />
                </span>
                <span className="font-head text-white">Facebook</span>
              </a>
              <a
                href={BRAND.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Gormans Contracting on Instagram"
                className="group flex items-center gap-3 rounded-sm border border-[#A8AEB2]/20 bg-[#14171A] px-5 py-3 transition-colors hover:border-[#7AB648]"
                data-testid="contact-instagram"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-sm bg-[#1E2227] text-[#7AB648] transition-colors group-hover:bg-[#7AB648] group-hover:text-[#14171A]">
                  <Instagram size={18} />
                </span>
                <span className="font-head text-white">Instagram</span>
              </a>
            </div>
          </div>
        </div>

        {/* Right — contact form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-6"
        >
          <form
            onSubmit={submit}
            className="rounded-sm border border-[#A8AEB2]/15 bg-[#14171A] p-6 md:p-10"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 gap-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-white font-body">Name *</Label>
                  <Input
                    className={inputClass}
                    placeholder="Your name"
                    value={form.name}
                    onChange={update("name")}
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white font-body">Phone</Label>
                  <Input
                    className={inputClass}
                    placeholder="Your phone"
                    value={form.phone}
                    onChange={update("phone")}
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-white font-body">Email *</Label>
                <Input
                  type="email"
                  className={inputClass}
                  placeholder="you@email.com"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="contact-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white font-body">Message *</Label>
                <Textarea
                  className="bg-[#1E2227] border-[#A8AEB2]/25 text-white placeholder:text-[#A8AEB2]/60 focus-visible:ring-[#7AB648] focus-visible:ring-offset-0 rounded-sm min-h-[140px]"
                  placeholder="How can we help?"
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-message-input"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-[#7AB648] px-10 py-4 font-head font-bold uppercase tracking-wide text-[#14171A] transition-colors hover:bg-[#6BA339] active:scale-95 disabled:opacity-60"
              data-testid="contact-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Sending...
                </>
              ) : (
                "Send Message"
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
